/**
 * 克隆循环 Swiper 公共方法
 * 用于 activity-swiper-video / index3-new-youtube-swiper：
 * - 前后各克隆一组 slide，实现可拖拽循环
 * - 胶囊/圆点分页同步
 * - 可选 prev/next 按钮
 *
 * 依赖：window.Swiper、window.SingcoreCapsulePagination
 */
(function (global) {
  'use strict';

  var DEFAULTS = {
    slidesPerView: 'auto',
    slidesPerGroup: 1,
    spaceBetween: 16,
    speed: 400,
    loop: false,
    watchOverflow: false,
    allowTouchMove: true,
    simulateTouch: true,
    grabCursor: true,
    resistanceRatio: 0.65,
    observer: true,
    observeParents: true,
  };

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  /**
   * 清除上次克隆节点
   * @param {Element} wrapper
   * @param {string} cloneAttr 如 data-avs-clone / data-yt-clone
   */
  function clearClones(wrapper, cloneAttr) {
    if (!wrapper || !cloneAttr) return;
    wrapper.querySelectorAll('[' + cloneAttr + ']').forEach(function (node) {
      node.remove();
    });
  }

  /**
   * 在 wrapper 前后各克隆一组 originals
   * @param {Object} options
   * @param {Element} options.wrapper
   * @param {Element[]} options.originals
   * @param {string} options.cloneAttr
   * @param {string} [options.clearSlideAttr] 克隆时移除的属性（如 data-avs-slide）
   */
  function appendLoopClones(options) {
    var opts = options || {};
    var wrapper = opts.wrapper;
    var originals = opts.originals || [];
    var cloneAttr = opts.cloneAttr;
    var clearSlideAttr = opts.clearSlideAttr;

    if (!wrapper || !cloneAttr || originals.length < 2) return;

    originals.forEach(function (slide) {
      var clone = slide.cloneNode(true);
      if (clearSlideAttr) clone.removeAttribute(clearSlideAttr);
      clone.setAttribute(cloneAttr, 'tail');
      wrapper.appendChild(clone);
    });

    for (var i = originals.length - 1; i >= 0; i--) {
      var headClone = originals[i].cloneNode(true);
      if (clearSlideAttr) headClone.removeAttribute(clearSlideAttr);
      headClone.setAttribute(cloneAttr, 'head');
      wrapper.insertBefore(headClone, wrapper.firstChild);
    }
  }

  /**
   * 将 activeIndex 校正回中间组，并同步分页
   */
  function createNormalizeLoop(options) {
    var enableLoop = !!options.enableLoop;
    var baseCount = options.baseCount || 0;
    var pagination = options.pagination;
    var normalizing = false;

    return {
      isNormalizing: function () {
        return normalizing;
      },
      run: function (instance) {
        if (!enableLoop || normalizing || !instance) return;
        var idx = instance.activeIndex;
        if (idx < baseCount) {
          normalizing = true;
          instance.slideTo(idx + baseCount, 0, false);
          normalizing = false;
        } else if (idx >= baseCount * 2) {
          normalizing = true;
          instance.slideTo(idx - baseCount, 0, false);
          normalizing = false;
        }
        if (pagination && typeof pagination.setActive === 'function') {
          pagination.setActive(
            global.SingcoreCapsulePagination.logicalFromClonedIndex(
              instance.activeIndex,
              baseCount,
              enableLoop
            )
          );
        }
      },
    };
  }

  /**
   * 同步 prev/next 禁用态：到头置灰，不可再滑
   */
  function syncNavDisabled(swiper, prevBtn, nextBtn, locked) {
    if (!swiper) return;
    var lockEnds = locked !== false;
    var atStart = lockEnds ? !!swiper.isBeginning : false;
    var atEnd = lockEnds ? !!swiper.isEnd : false;

    function apply(btn, disabled) {
      if (!btn) return;
      btn.disabled = disabled;
      btn.classList.toggle('is-disabled', disabled);
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    apply(prevBtn, atStart);
    apply(nextBtn, atEnd);
  }

  /**
   * 绑定 prev / next，并在非循环时同步置灰
   * @returns {function(): void} unbind
   */
  function bindNavButtons(swiper, prevBtn, nextBtn, options) {
    var opts = options || {};
    var lockAtEnds = opts.lockAtEnds !== false && !opts.enableLoop;
    var handlers = [];

    function bind(btn, fn) {
      if (!btn) return;
      var handler = function () {
        if (btn.disabled) return;
        fn();
      };
      btn.addEventListener('click', handler);
      handlers.push({ btn: btn, handler: handler });
    }

    bind(prevBtn, function () {
      swiper.slidePrev();
    });
    bind(nextBtn, function () {
      swiper.slideNext();
    });

    function onUpdate() {
      syncNavDisabled(swiper, prevBtn, nextBtn, lockAtEnds);
    }

    if (swiper && typeof swiper.on === 'function') {
      swiper.on('init', onUpdate);
      swiper.on('slideChange', onUpdate);
      swiper.on('reachBeginning', onUpdate);
      swiper.on('reachEnd', onUpdate);
      swiper.on('fromEdge', onUpdate);
      swiper.on('resize', onUpdate);
      onUpdate();
    }

    return function unbind() {
      handlers.forEach(function (item) {
        item.btn.removeEventListener('click', item.handler);
      });
      if (swiper && typeof swiper.off === 'function') {
        swiper.off('init', onUpdate);
        swiper.off('slideChange', onUpdate);
        swiper.off('reachBeginning', onUpdate);
        swiper.off('reachEnd', onUpdate);
        swiper.off('fromEdge', onUpdate);
        swiper.off('resize', onUpdate);
      }
    };
  }

  function assign(target) {
    var out = target || {};
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      if (!src) continue;
      Object.keys(src).forEach(function (key) {
        out[key] = src[key];
      });
    }
    return out;
  }

  /**
   * 创建带克隆循环的 Swiper
   * @param {Object} options
   * @param {Element} options.el Swiper 根节点
   * @param {Element} [options.wrapper] 默认 el 内 .swiper-wrapper
   * @param {string} [options.slideSelector='.swiper-slide'] 原始 slide 选择器
   * @param {string} options.cloneAttr 克隆标记属性名
   * @param {string} [options.clearSlideAttr] 克隆时移除的属性
   * @param {Element[]|NodeList} [options.dots] 分页圆点
   * @param {Element} [options.prevBtn]
   * @param {Element} [options.nextBtn]
   * @param {number} [options.spaceBetween=16]
   * @param {Object} [options.breakpoints]
   * @param {Object} [options.swiperOptions] 额外 Swiper 配置（会覆盖默认）
   * @returns {{ swiper: Object, pagination: Object|null, baseCount: number, enableLoop: boolean, destroy: function(): void }|null}
   */
  function create(options) {
    var opts = options || {};
    var el = opts.el;
    var Capsule = global.SingcoreCapsulePagination;

    if (!el || typeof global.Swiper === 'undefined' || !Capsule) return null;

    if (el.swiper) {
      el.swiper.destroy(true, true);
    }

    var wrapper = opts.wrapper || el.querySelector('.swiper-wrapper');
    if (!wrapper) return null;

    var cloneAttr = opts.cloneAttr;
    if (!cloneAttr) return null;

    clearClones(wrapper, cloneAttr);

    var slideSelector = opts.slideSelector || '.swiper-slide';
    var originals = toArray(wrapper.querySelectorAll(slideSelector)).filter(function (node) {
      return !node.hasAttribute(cloneAttr);
    });
    var baseCount = originals.length;
    if (!baseCount) return null;

    var enableLoop =
      opts.enableLoop != null ? !!opts.enableLoop && baseCount > 1 : baseCount > 1;
    var dots = toArray(opts.dots);
    var pagination = null;
    var swiper = null;

    if (dots.length) {
      pagination = Capsule.create({
        dots: dots,
        count: baseCount,
        onSelect: function (index) {
          var target = enableLoop ? index + baseCount : index;
          if (swiper) swiper.slideTo(target);
        },
      });
    }

    if (enableLoop) {
      appendLoopClones({
        wrapper: wrapper,
        originals: originals,
        cloneAttr: cloneAttr,
        clearSlideAttr: opts.clearSlideAttr,
      });
    }

    var normalize = createNormalizeLoop({
      enableLoop: enableLoop,
      baseCount: baseCount,
      pagination: pagination,
    });

    var swiperConfig = assign({}, DEFAULTS, {
      spaceBetween: opts.spaceBetween != null ? opts.spaceBetween : DEFAULTS.spaceBetween,
      initialSlide: enableLoop ? baseCount : 0,
      breakpoints: opts.breakpoints || {
        0: {
          slidesPerView: 'auto',
          slidesPerGroup: 1,
          spaceBetween: opts.spaceBetween != null ? opts.spaceBetween : 16,
        },
      },
      on: {
        slideChange: function () {
          if (normalize.isNormalizing()) return;
          if (pagination) {
            pagination.setActive(
              Capsule.logicalFromClonedIndex(this.activeIndex, baseCount, enableLoop)
            );
          }
        },
        touchEnd: function () {
          normalize.run(this);
        },
        transitionEnd: function () {
          normalize.run(this);
        },
      },
    });

    if (opts.swiperOptions) {
      var extraOn = opts.swiperOptions.on;
      assign(swiperConfig, opts.swiperOptions);
      if (extraOn) {
        swiperConfig.on = assign({}, swiperConfig.on, extraOn);
      }
    }

    swiper = new global.Swiper(el, swiperConfig);
    var unbindNav = bindNavButtons(swiper, opts.prevBtn, opts.nextBtn, {
      enableLoop: enableLoop,
      lockAtEnds: opts.lockNavAtEnds !== false,
    });

    return {
      swiper: swiper,
      pagination: pagination,
      baseCount: baseCount,
      enableLoop: enableLoop,
      destroy: function () {
        unbindNav();
        if (pagination && typeof pagination.unbind === 'function') {
          pagination.unbind();
        }
        if (swiper && !swiper.destroyed) {
          swiper.destroy(true, true);
        }
        clearClones(wrapper, cloneAttr);
      },
    };
  }

  global.SingcoreCloneSwiper = {
    clearClones: clearClones,
    appendLoopClones: appendLoopClones,
    createNormalizeLoop: createNormalizeLoop,
    syncNavDisabled: syncNavDisabled,
    bindNavButtons: bindNavButtons,
    create: create,
  };
})(typeof window !== 'undefined' ? window : this);
